import { observer } from 'mobx-react-lite'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import { useInjection } from '@core/providers/inversify-react.provider'
import { ThemeToggleComponent } from '@core/infra/theme/theme-toggle.component'
import { AdminModeToggleComponent } from '@core/infra/authentication/admin-mode-toggle.component'
import { LanguageSwitcherComponent } from '@core/infra/i18n/language-switcher.component'
import { CommandPalettePresenter } from '@core/infra/command-palette/command-palette.presenter'
import { InboxBellComponent } from '@core/infra/inbox/inbox-bell.component'
import { WorkspaceSwitcherComponent } from '@core/infra/workspace/workspace-switcher.component'
import { ShellPresenter } from '../shell.presenter'
import { AvatarMenuComponent } from './avatar-menu.component'

export const TopBarComponent = observer(() => {
  const shellPresenter: ShellPresenter = useInjection(ShellPresenter)
  const commandPalettePresenter: CommandPalettePresenter = useInjection(CommandPalettePresenter)
  const vm = shellPresenter.viewModel

  return (
    <div className="sticky top-0 z-50 flex h-16 shrink-0 items-center gap-x-4 border-b border-shell-border bg-shell-surface px-4 shadow-xs sm:gap-x-6 sm:px-6">
      {vm.showChrome && (
        <>
          <button
            type="button"
            onClick={() => shellPresenter.toggleSidebar()}
            className="-m-2.5 flex items-center justify-center rounded-md p-2.5 text-shell-ink-secondary hover:text-shell-ink"
            aria-label={vm.toggleSidebarLabel}
            title={vm.toggleSidebarLabel}
          >
            {vm.sidebarOpen ? (
              <XMarkIcon aria-hidden="true" className="size-6" />
            ) : (
              <Bars3Icon aria-hidden="true" className="size-6" />
            )}
          </button>

          <div aria-hidden="true" className="h-6 w-px bg-shell-border" />
        </>
      )}

      <div className="flex min-w-0 flex-1 items-center gap-x-4 self-stretch sm:gap-x-6">
        <span className="min-w-0 truncate text-base font-semibold text-shell-ink">{vm.appName}</span>

        <div className="ml-auto flex shrink-0 items-center gap-x-4 sm:gap-x-6">
          {vm.showChrome && (
            <button
              type="button"
              onClick={() => commandPalettePresenter.open()}
              aria-label={vm.searchLabel}
              className="hidden w-64 items-center gap-x-2 rounded-md bg-shell-surface px-2.5 py-1.5 text-base text-shell-ink-muted outline-1 -outline-offset-1 outline-shell-border hover:outline-shell-accent sm:flex"
            >
              <MagnifyingGlassIcon aria-hidden="true" className="size-5 shrink-0" />
              <span className="flex-1 text-left sm:text-sm/6">{vm.searchLabel}</span>
              <kbd className="hidden shrink-0 rounded border border-shell-border px-1 text-xs font-sans sm:inline">
                ⌘K
              </kbd>
            </button>
          )}

          <div className="flex items-center gap-x-1">
            {vm.showChrome && <WorkspaceSwitcherComponent />}
            <InboxBellComponent />
            <AdminModeToggleComponent />
            <LanguageSwitcherComponent />
            <ThemeToggleComponent />
            <AvatarMenuComponent />
          </div>
        </div>
      </div>
    </div>
  )
})
